import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { type ReactNode, useMemo } from 'react';

import {
  FUNNEL_CONFIGS,
  apiBackedFeatures,
} from '@features/Quiz/constants/configurations';
import { useGetStripeProductId } from '@features/Stripe';

import {
  selectedPlanAtom,
  stripePaymentErrorAtom,
} from '@features/Stripe/atoms';
import { useAtomValue } from 'jotai';
import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { SHORT_LINK_PAY_TEST_NAME } from 'src/constants/analytics';
import { routes } from 'src/constants/navigation';
import {
  JAPANESE_WALKING_2_VARIANTS,
  JAPANESE_WALKING_VARIANTS,
  TAI_CHI_VARIANTS,
  type TpStripeVariants,
} from 'src/constants/variants';
import { FlagsProvider } from './FlaggedUtil';

export const FEATURE_FLAGS = [
  'introOffer',
  'comboPaywall',
  'usFeature',
  'shortWallPilatesFeature',
  'stepsFromApi',
  'shortLinkPayFeature',
  'fbPixelSept2025Feature',
  'isBundleFeature',
  'superYouthUpgradePaywall',
  'taiChiFeature',
  'japaneseWalkingFeature',
  'japaneseWalking2Feature',
] as const;

const startsWithB = (inputString: any): boolean => /^B/.test(inputString || '');

export const FeatureFlagProvider = observer(
  ({ children }: { children: ReactNode }) => {
    const {
      authStore: { variant },
      analyticsStore: { getCaseNameByTestName },
    } = useStores();
    const { wasPaymentDeclinedOfferAccepted = false } = useAtomValue(
      stripePaymentErrorAtom,
    );

    const { pathname } = useRouter();

    const isCheapOfferFeatureEnabled = variant === 'variant19';

    const selectedPlanInfo = useAtomValue(selectedPlanAtom);

    const { isUsProductIdValid } = useGetStripeProductId({
      variant: (variant as TpStripeVariants) ?? ('default' as TpStripeVariants),
      enabled:
        isCheapOfferFeatureEnabled ||
        pathname === routes.newPlanPaywall ||
        pathname === routes.youthPremium,
    });

    const isVariantUsEnabled = isUsProductIdValid;

    const isTaiChiFeatureEnabled = TAI_CHI_VARIANTS.some(
      (v) => v.variant_name === variant,
    );
    const isJapaneseWalkingFeatureEnabled = JAPANESE_WALKING_VARIANTS.some(
      (v) => v.variant_name === variant,
    );
    const isJapaneseWalking2FeatureEnabled = JAPANESE_WALKING_2_VARIANTS.some(
      (v) => v.variant_name === variant,
    );
    const isShortWallPilatesFeatureEnabled =
      !isTaiChiFeatureEnabled &&
      !isJapaneseWalkingFeatureEnabled &&
      !isJapaneseWalking2FeatureEnabled;

    const isComboPaywallEnabled = true;

    const introOfferWallPilatesDeclineEnabled =
      isShortWallPilatesFeatureEnabled && wasPaymentDeclinedOfferAccepted;

    const isIntroOfferEnabled =
      isComboPaywallEnabled && !introOfferWallPilatesDeclineEnabled;

    const isBundleFeatureEnabled = !!selectedPlanInfo.bundle;

    const isShortLinkPayEnabled = startsWithB(
      getCaseNameByTestName(SHORT_LINK_PAY_TEST_NAME),
    );

    const isFbPixelSept2025Enabled = variant === 'WP20FBShrtPix';

    const features = useMemo((): Record<
      (typeof FEATURE_FLAGS)[number],
      boolean
    > => {
      const flagMap: Record<string, boolean> = {
        cheapOfferFeature: isCheapOfferFeatureEnabled,
        shortWallPilatesFeature: isShortWallPilatesFeatureEnabled,
        taiChiFeature: isTaiChiFeatureEnabled,
        japaneseWalkingFeature: isJapaneseWalkingFeatureEnabled,
        japaneseWalking2Feature: isJapaneseWalking2FeatureEnabled,
      };

      const enabledFeature = Object.values(FUNNEL_CONFIGS).find(
        ({ featureFlag }) => flagMap[featureFlag],
      );

      return {
        comboPaywall: isComboPaywallEnabled,
        introOffer: isIntroOfferEnabled,
        usFeature: isVariantUsEnabled,
        isBundleFeature: isBundleFeatureEnabled,
        taiChiFeature: isTaiChiFeatureEnabled,
        japaneseWalkingFeature: isJapaneseWalkingFeatureEnabled,
        japaneseWalking2Feature: isJapaneseWalking2FeatureEnabled,
        stepsFromApi: enabledFeature
          ? apiBackedFeatures.includes(enabledFeature.featureName)
          : false,
        shortWallPilatesFeature: isShortWallPilatesFeatureEnabled,
        shortLinkPayFeature: isShortLinkPayEnabled,
        fbPixelSept2025Feature: isFbPixelSept2025Enabled,
        superYouthUpgradePaywall: variant === 'superYouthUpgradePaywall',
      };
    }, [
      isFbPixelSept2025Enabled,
      isJapaneseWalking2FeatureEnabled,
      isCheapOfferFeatureEnabled,
      isVariantUsEnabled,
      variant,
      isIntroOfferEnabled,
      isJapaneseWalkingFeatureEnabled,
      isShortLinkPayEnabled,
      isShortWallPilatesFeatureEnabled,
      isTaiChiFeatureEnabled,
      isBundleFeatureEnabled,
    ]);

    return <FlagsProvider features={features}>{children}</FlagsProvider>;
  },
);
